import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'; // The Differential
import './App.css';
import {
  Clock, Activity, Trophy, ShieldAlert, CheckCircle,
  AlertTriangle, AlertCircle, FileText, ChevronRight, ChevronLeft, Play,
  Archive, HelpCircle, X, XCircle,
  TestTubeDiagonal, ScanLine, Syringe, Stethoscope, Pill, ShieldCheck,
  Flame, WifiOff, BarChart2, Users, Share2, Zap, MessageCircle, Sun, Moon, Search,
} from 'lucide-react';
import {
  loadGameState, saveGameState, initGameState,
  getElapsedSeconds, saveToLeaderboard, pushToArchive, getGradeLabel, getArchive,
  getStreakData, updateStreak, reconcileStreak, hasSubmittedStats, markStatsSubmitted, clearStatsSubmitted,
} from './utils/storage';
import { fetchGame, fetchSuggestions, sendAction, submitGuess, fetchGameByDay, fetchDiseases, submitStats, fetchStats } from './utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// THEME — persists dark/light preference to localStorage
// ─────────────────────────────────────────────────────────────────────────────
const THEME_KEY = 'thediff_theme';
function useTheme() {
  const [theme, setTheme] = React.useState(() => {
    try { return localStorage.getItem(THEME_KEY) || 'dark'; } catch { return 'dark'; }
  });
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : '');
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  }, [theme]);
  const toggle = React.useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);
  return [theme, toggle];
}

// ─────────────────────────────────────────────────────────────────────────────
// EDITOR MODE DETECTION — /editor path
// Security: bots/scrapers are actively blocked from rendering the editor.
// The path itself is disallowed in robots.txt and noindexed server-side.
// ─────────────────────────────────────────────────────────────────────────────
const _isEditorPath =
  window.location.pathname === '/editor' ||
  window.location.pathname.startsWith('/editor/');

// Bot user-agent detection — prevents automated headless browsers and scanners
// from ever rendering the editor UI even if they hit the path.
const _isBot = (() => {
  try {
    const ua = navigator.userAgent.toLowerCase();
    return /bot|crawl|spider|slurp|teoma|archive|facebookexternalhit|whatsapp|semrush|ahrefs|mj12|dotbot|yandex|baidu|bytespider|gptbot|anthropic|claudebot|perplexity|ccbot|dataminr|masscan|nmap|sqlmap|nikto|w3af|zgrab/i.test(ua);
  } catch { return false; }
})();

const IS_EDITOR_MODE = _isEditorPath && !_isBot;

// /privacy and /terms path detection — renders policy pages at real URLs
const IS_PRIVACY_PAGE = window.location.pathname === '/privacy';
const IS_TERMS_PAGE = window.location.pathname === '/terms';

// ?playDay=N — used by editor to launch a specific day's case in a new tab
const PLAY_DAY_OVERRIDE = (() => {
  try {
    const p = new URLSearchParams(window.location.search).get('playDay');
    return p ? parseInt(p, 10) : null;
  } catch { return null; }
})();


// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const TICON = {
  lab: <TestTubeDiagonal size={14} color="var(--blue)" />,
  imaging: <ScanLine size={14} color="var(--purple)" />,
  procedure: <Syringe size={14} color="var(--red)" />,
  exam: <Stethoscope size={14} color="var(--green)" />,
  medication: <Pill size={14} color="var(--amber)" />
};
const icon = t => TICON[t] || <FileText size={14} />;

const fmt = s => { const m = Math.floor(s / 60), r = s % 60; return m ? `${m}:${String(r).padStart(2, '0')}` : `${s}s`; };
const fmtLg = s => { const m = Math.floor(s / 60), r = s % 60; return m ? `${m}m ${String(r).padStart(2, '0')}s` : `${s}s`; };

const GRADES = [
  { min: 90, l: 'S', c: '#22c55e' },
  { min: 75, l: 'A', c: '#60a5fa' },
  { min: 55, l: 'B', c: '#a78bfa' },
  { min: 30, l: 'C', c: '#f59e0b' },
  { min: 1, l: 'D', c: '#ef4444' },
  { min: 0, l: 'F', c: '#ef4444' },
];
const getGrade = s => GRADES.find(g => (s ?? 0) >= g.min) || GRADES[5];

function makeShare(day, moves, time, score, fast, g, isLost) {
  const url = "https://thediff.icu";
  if (isLost) {
    return [
      `The Differential — Day #${day}`,
      `Time expired. Patient lost.`,
      `${moves} move${moves !== 1 ? 's' : ''} · ${fmtLg(time)}`,
      `🟥`.repeat(Math.min(moves, 8)),
      `#TheDiff`,
      url
    ].join('\n');
  }

  // Create a grid of squares: one for each move.
  // Last one is green (diagnosis confirmed), others are orange (findings).
  const squaresPerRow = 5;
  const totalSquares = Math.min(moves, 20); // Cap it so it doesn't span forever
  let grid = "";
  for (let i = 0; i < totalSquares; i++) {
    grid += (i === moves - 1) ? "🟩" : "🟧";
    if ((i + 1) % squaresPerRow === 0 && i !== totalSquares - 1) grid += "\n";
  }

  return [
    `The Differential — Day #${day}${fast ? ' ⚡' : ''}`,
    `Grade: ${g.l} · Score: ${score}`,
    `${moves} move${moves !== 1 ? 's' : ''} · ${fmtLg(time)}`,
    ``,
    grid,
    ``,
    `#TheDiff #ClinicalParsimony`,
    url
  ].join('\n');
}


// ─── COMMUNITY CARD (WhatsApp Channel) ─────────────────────────────────────────
function CommunityCard({ isEndScreen }) {
  const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029VbCMz8bKgsNkGaKxsN06"; // Placeholder

  return (
    <div className="community-card">
      {isEndScreen && (
        <div style={{ fontSize: '0.8rem', color: 'var(--mid)', marginBottom: 12, textAlign: 'center', lineHeight: 1.4 }}>
          Get a ping the moment a new case is live.
        </div>
      )}
      <div className="community-card-top">
        <div className="community-header-left">
          <MessageCircle size={20} className="community-icon-main" style={{ color: 'var(--green)' }} />
          <div className="community-card-title">Join The Differential</div>
        </div>
      </div>
      <div className="community-card-benefits">
        <div className="community-benefit">
          <Zap size={12} className="benefit-icon" style={{ color: 'var(--green)' }} />
          <span>Instant alerts for every new case drop</span>
        </div>
        <div className="community-benefit">
          <ShieldCheck size={12} className="benefit-icon" style={{ color: 'var(--green)' }} />
          <span>Professional community. Zero ads. No noise.</span>
        </div>
      </div>

      <a
        href={WHATSAPP_CHANNEL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="community-card-btn"
      >
        Join the WhatsApp Channel
      </a>

      <div className="community-card-privacy" style={{ textAlign: 'center', marginTop: 12 }}>
        Privacy: Your phone number and profile are completely hidden from us and other members.
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// PATIENT PANEL
// ─────────────────────────────────────────────────────────────────────────────
function formatVitals(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  const p = [];
  if (v.heart_rate) p.push('HR ' + v.heart_rate);
  if (v.blood_pressure) p.push('BP ' + v.blood_pressure);
  if (v.respiratory_rate) p.push('RR ' + v.respiratory_rate);
  if (v.spo2) p.push('SpO2 ' + v.spo2);
  if (v.temperature) p.push('T ' + v.temperature);
  if (v.gcs) p.push('GCS ' + v.gcs);
  return p.join(' | ') || null;
}

function PatientPanel({ patient }) {
  const [open, setOpen] = useState(false);
  if (!patient) return null;
  const vitalsStr = formatVitals(patient.vitals);

  return (
    <div className="patient-panel">
      <div className="pp-header" onClick={() => setOpen(o => !o)} role="button" aria-expanded={open}>
        <div className="pp-badge">{patient.age}{patient.sex}</div>
        <div className="pp-header-main">
          <div className="pp-complaint-label">Chief Complaint</div>
          <div className="pp-complaint">{patient.chief_complaint}</div>
          <div className="pp-toggle-text">
            View Patient Chart <ChevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--mid)' }} />
          </div>
        </div>
      </div>

      {open && (
        <>
          <div className="pp-overlay-backdrop" onClick={() => setOpen(false)} />
          <div className="pp-overlay">
            <div className="pp-overlay-inner">
              {patient.history && (
                <div className="pp-section full">
                  <div className="pp-section-label">History & Background</div>
                  <div className="pp-section-text">{patient.history}</div>
                </div>
              )}
              {patient.physical_exam && (
                <div className="pp-section full">
                  <div className="pp-section-label">Examination Findings</div>
                  <div className="pp-section-text">{patient.physical_exam}</div>
                </div>
              )}
              {vitalsStr && (
                <div className="pp-section full">
                  <div className="pp-section-label">Vitals</div>
                  <div className="pp-section-text mono">{vitalsStr}</div>
                </div>
              )}
              <button className="pp-close-btn" onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// RESULT ENTRY — shows test name + clinical finding + snarky comment
// ─────────────────────────────────────────────────────────────────────────────
function ResultEntry({ entry, isLatest }) {
  const { type, display_name, action, moves_delta, message, sub_result, ego_boost, comment } = entry;

  const isAlreadyDone = type === 'already_done';
  const cardClass = ['result-entry', isLatest ? 'latest' : '', `s-${type}`].filter(Boolean).join(' ');

  let clinicalText = null;
  let colorText = null;

  if (type === 'snark' || type === 'clown') {
    if (sub_result) {
      clinicalText = sub_result;
      colorText = message;
    } else {
      colorText = message;
    }
  } else if (type === 'finding') {
    clinicalText = message;
    colorText = ego_boost;
  } else if (type === 'relevant') {
    clinicalText = message;
    colorText = comment;
  } else {
    clinicalText = message;
  }

  return (
    <div className={cardClass}>
      <div className="re-header">
        <div className={`re-color-bar ${type}`} />
        <span className={`re-name${type === 'finding' ? ' finding' : ''}`}>
          {display_name || action}
        </span>
        {type === 'finding' && <span className="re-badge key">Expert Move</span>}
        {type === 'relevant' && <span className="re-badge relevant">Relevant</span>}
        {moves_delta > 0 && <span className="re-badge trap"><AlertTriangle size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} /> +{moves_delta} move{moves_delta > 1 ? 's' : ''}</span>}
      </div>
      <div className="re-body">
        {isAlreadyDone ? (
          <div className="re-dim">Already ordered — review your test log.</div>
        ) : (
          <>
            {clinicalText && <div className="re-finding">{clinicalText}</div>}
            {colorText && <div className={`re-comment ${type}`}>{colorText}</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEED
// ─────────────────────────────────────────────────────────────────────────────
function Feed({ entries }) {
  const feedRef = useRef(null);
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [entries]);

  if (!entries || entries.length === 0) {
    return <div className="feed"><div className="feed-empty"><div className="fe-main">Order your first test to begin.</div></div></div>;
  }
  return (
    <div className="feed" ref={feedRef}>
      {entries.map((e, i) => (
        <ResultEntry key={i} entry={e} isLatest={i === entries.length - 1} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUGGESTIONS
// ─────────────────────────────────────────────────────────────────────────────
function Suggestions({ list, query, hiIdx, onSelect, onHover }) {
  if (!list) return null;
  if (list.length === 0) {
    if (!query || query.trim().length < 2) return null;
    return <div className="sug-drop"><div className="sug-none">⚠ No match — check spelling</div></div>;
  }
  return (
    <div className="sug-drop">
      {list.map((s, i) => (
        <div key={s.slug} className={`sug-item${i === hiIdx ? ' hi' : ''}`}
          onMouseDown={e => { e.preventDefault(); onSelect(s); }}
          onMouseEnter={() => onHover(i)}
        >
          <span className="sug-icon">{icon(s.type)}</span>
          <span className="sug-name">{s.displayName}</span>
          <span className="sug-cat">{(s.category || '').replace(/_/g, ' ')}</span>
        </div>
      ))}
    </div>
  );
}

const LOSING_COMMENTS = [
  "Time expired. The patient gave up waiting and went to another clinic.",
  "You snatched defeat from the jaws of a totally treatable disease.",
  "The morbidity and mortality committee would like a word.",
  "Did your WiFi disconnect, or did your brain?",
  "You just proved that sometimes, doing nothing is actually the worst possible move.",
  "Risk management is currently drafting your termination letter.",
  "The patient decided to just google their symptoms instead. They probably got a better answer.",
  "You stood there frozen. The patient has been transferred to a more competent provider.",
  "You didn't just drop the ball; you threw it into the hospital incinerator.",
  "This isn't a spectator sport, doctor. You actually have to order things.",
  "Please hand your stethoscope and badge to the nearest security guard.",
  "You treated a waiting patient like a piece of abstract art. Just staring at it.",
  "The patient's chart has been updated to reflect your profound lack of urgency.",
  "I’ve seen glaciers move faster than your clinical decision-making.",
  "Your medical degree called. It wants to be returned to the cereal box you found it in.",
  "The patient has successfully aged out of the demographic for this issue while waiting for you.",
  "Did your fall asleep with your eyes open, or is this just your resting diagnostic face?",
  "We replaced you with a Magic 8-Ball. Patient outcomes have improved by forty percent.",
  "The attending physician just face-palmed so hard they gave themselves a concussion.",
  "Congratulations, you’ve discovered a brand new mechanism of injury: physician apathy.",
  "The patient walked out, cured by the sheer adrenaline of fleeing your care.",
  "Please step away from the patient. You are actively lowering the room’s average IQ.",
  "Are you waiting for a divine revelation? Because the lab sure isn't sending one.",
  "You’ve successfully turned a simple outpatient visit into a hostage situation.",
  "The nursing staff started a betting pool on when you'd do something. Everyone lost.",
  "You have the diagnostic instincts of a deer caught in headlights.",
  "WebMD just sent a cease-and-desist letter asking you to stop making them look good.",
  "The patient is now writing a strongly worded Yelp review from the lobby.",
  "If hesitation were a medical specialty, you’d be the Chief of Staff.",
  "Your clinical intuition is currently buffering. Please restart your brain.",
  "The patient's immune system gave up waiting for you and decided to handle it solo.",
  "We are revoking your prescribing privileges. You are now only allowed to prescribe rest and fluids.",
  "Even a broken clock is right twice a day. You, however, are just broken.",
  "The medical board is reconsidering the 'do no harm' oath just for you.",
  "You stared at the patient so long it became socially awkward, and then clinically negligent.",
  "Your strategy of 'ignore it until it goes away' has failed spectacularly.",
  "We’d ask you to turn in your badge, but we aren't sure you'd know how to unclip it.",
  "The janitor just stepped in and offered a better differential diagnosis.",
  "You treat medical emergencies with the urgency of a sloth on vacation.",
  "The patient left to seek a second opinion from a passing pigeon.",
  "Please stop playing doctor, the actual doctors need the room.",
  "You’ve achieved the rare medical milestone of negative progress.",
  "Your diagnostic process is a fascinating study in aggressive mediocrity.",
  "We put a houseplant in your chair. The patients say it has a better bedside manner.",
  "You missed the diagnosis so widely, it landed in a different hospital.",
  "The only thing you managed to cure today was the patient's faith in the healthcare system.",
  "We were going to page you, but realized silence is actually safer for the patients.",
  "The patient healed themselves out of sheer spite.",
  "Your medical logic resembles a pretzel, but significantly less useful.",
  "A localized blackout occurred, entirely contained within your prefrontal cortex.",
  "You approached this case with the fierce determination of a wet paper towel.",
  "The simulation has ended to spare the digital patient any further embarrassment."
];

function getRandomLosingComment() {
  return LOSING_COMMENTS[Math.floor(Math.random() * LOSING_COMMENTS.length)];
}

// ─────────────────────────────────────────────────────────────────────────────
// CASE DEBRIEF — patient info + diagnosis + manifestations (shown after game)
// ─────────────────────────────────────────────────────────────────────────────
function formatVitalsInline(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  const p = [];
  if (v.heart_rate) p.push('HR ' + v.heart_rate);
  if (v.blood_pressure) p.push('BP ' + v.blood_pressure);
  if (v.respiratory_rate) p.push('RR ' + v.respiratory_rate);
  if (v.spo2) p.push('SpO\u2082 ' + v.spo2);
  if (v.temperature) p.push('T ' + v.temperature);
  if (v.gcs) p.push('GCS ' + v.gcs);
  return p.join(' \u00b7 ') || null;
}

function CaseDebrief({ caseInfo, diagnosis }) {
  const patient = caseInfo?.patient || null;
  const dp = caseInfo?.disease_presentations || null;
  const manifestations = dp?.manifestations || [];
  if (!patient && !dp) return null;
  const vitalsStr = formatVitalsInline(patient?.vitals);
  return (
    <div className="case-debrief-card">
      <div className="cd-head">Case Overview</div>
      {patient && (
        <>
          {patient.chief_complaint && (
            <div className="cd-section">
              <div className="cd-label">Chief Complaint</div>
              <div className="cd-text">{patient.chief_complaint}</div>
            </div>
          )}
          {patient.history && (
            <div className="cd-section">
              <div className="cd-label">History</div>
              <div className="cd-text">{patient.history}</div>
            </div>
          )}
          {patient.physical_exam && (
            <div className="cd-section">
              <div className="cd-label">Physical Exam</div>
              <div className="cd-text">{patient.physical_exam}</div>
            </div>
          )}
          {vitalsStr && (
            <div className="cd-section">
              <div className="cd-label">Vitals</div>
              <div className="cd-text cd-mono">{vitalsStr}</div>
            </div>
          )}
        </>
      )}
      {(diagnosis || dp?.diagnosis) && (
        <div className="cd-diagnosis">
          <div className="cd-dx-label">Diagnosis</div>
          <div className="cd-dx">{diagnosis || dp?.diagnosis}</div>
        </div>
      )}
      {manifestations.length > 0 && (
        <div className="cd-section">
          <div className="cd-label">Key Manifestations</div>
          <ul className="cd-manifestations">
            {manifestations.map((m, i) => (
              <li key={i} className="cd-manif-item"><ChevronRight size={12} className="cd-manif-icon" />{m}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="cd-disclaimer">
        <AlertCircle size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-2px' }} />
        For educational and entertainment purposes only. Each case is medically reviewed for clinical accuracy. Not medical advice.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// END SCREEN
// ─────────────────────────────────────────────────────────────────────────────
const EndScreen = React.memo(({ gs, day, caseInfo, isArchiveReplay }) => {
  const [copiedShare, setCopiedShare] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [displayScore, setDisplayScore] = useState(0);  // animated score
  const [streak, setStreak] = useState(null);

  const isLost = gs.status === 'lost';
  const elapsed = (gs.end_time && gs.start_time) ? Math.floor((gs.end_time - gs.start_time) / 1000) : 0;
  const g = useMemo(() => isLost ? { l: 'F', c: '#ef4444' } : getGrade(gs.score), [isLost, gs.score]);

  const txtShare = useMemo(() => makeShare(day, gs.moves, elapsed, gs.score, gs.fast_solve, g, isLost),
    [day, gs.moves, elapsed, gs.score, gs.fast_solve, g, isLost]);

  const finalMessage = isLost
    ? (gs.final_status_message || "Time expired.")
    : (gs.win_message || 'Diagnosis confirmed. Well done.');

  // ── In-memory lock: ensures submitStats fires at most ONCE per EndScreen mount.
  // This is separate from the localStorage guard (hasSubmittedStats), which persists
  // across page reloads. The ref guards against the same-render race where
  // clearStatsSubmitted + re-submit both fire before localStorage can be re-set.
  const hasSubmittedThisMount = useRef(false);

  // ── On mount: streak, stats submit+fetch, score animation ──────────────────
  useEffect(() => {
    const targetScore = isLost ? 0 : (gs.score || 0);

    // 1. Animate score count-up
    if (targetScore > 0) {
      let start = null;
      const duration = 800;
      const step = (ts) => {
        if (!start) start = ts;
        const prog = Math.min((ts - start) / duration, 1);
        setDisplayScore(Math.round(prog * targetScore));
        if (prog < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    // 2. Live games only: streak, stats submit, global benchmark
    // Guard: archive replays never write stats.
    // Guard: each day is submitted exactly once per browser — no duplicates.
    if (!isArchiveReplay) {
      const updatedStreak = updateStreak(day);
      setStreak(updatedStreak || getStreakData());

      if (!hasSubmittedStats(day) && !hasSubmittedThisMount.current) {
        // Lock immediately — prevents any re-entrant call in this mount
        hasSubmittedThisMount.current = true;
        // Cap elapsed at 150s — backgrounded tabs can report inflated times.
        const cappedElapsed = Math.min(elapsed, 150);
        submitStats(day, gs.status, gs.moves, cappedElapsed, gs.score || 0, g.l)
          .then(ok => { if (ok) markStatsSubmitted(day); });
      } else if (!hasSubmittedThisMount.current) {
        // Already marked in localStorage — but verify the server has our data.
        // If global stats show 0 plays, our previous submission may have failed.
        // Clear the localStorage flag so the NEXT mount retries. Do NOT submit now.
        fetchStats(day).then(data => {
          if (!data?.total_plays || data.total_plays === 0) {
            clearStatsSubmitted(day);
          }
        }).catch(() => { });
      }

      fetchStats(day).then(data => {
        setGlobalStats(data);
        setStatsLoading(false);
      }).catch(() => setStatsLoading(false));
    } else {
      setStatsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyResults = () => navigator.clipboard.writeText(txtShare).then(() => {
    setCopiedShare(true); setTimeout(() => setCopiedShare(false), 2400);
  });

  return (
    <div className="win-shell">
      <div className="win-card">
        <div className="win-top">
          <div className="win-confirmed" style={isLost ? { color: 'var(--red)' } : {}}>
            {isLost ? <><XCircle size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} /> TIME EXPIRED</> :
              (gs.fast_solve ? <><Activity size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} /> Lightning Round</> :
                <><CheckCircle size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} /> Diagnosis Confirmed</>)}
          </div>
          <div className="win-dx">{isLost ? caseInfo?.exact_diagnosis : gs.diagnosis}</div>
        </div>
        <div className="win-comment">
          <div className="win-comment-bar" style={isLost ? { background: 'var(--red)' } : {}} />
          <div className="win-comment-inner">
            <div className="win-comment-text" style={isLost ? { color: 'var(--red)' } : {}}>{finalMessage}</div>
            <div className="win-comment-by" style={{ marginTop: '4px' }}>— The Differential</div>
          </div>
        </div>
        <div className="win-stats">
          <div className="win-stat">
            <div className="win-stat-label">Grade</div>
            <div className="win-stat-val grade-v" style={{ color: g.c }}>{g.l}</div>
          </div>
          <div className="win-stat">
            <div className="win-stat-label">Score</div>
            <div className="win-stat-val score-v" style={isLost ? { color: 'inherit' } : {}}>{isLost ? '—' : displayScore}</div>
          </div>
          <div className="win-stat">
            <div className="win-stat-label">Moves</div>
            <div className="win-stat-val">{gs.moves}</div>
          </div>
          <div className="win-stat">
            <div className="win-stat-label">Time</div>
            <div className="win-stat-val time-v">{fmtLg(elapsed)}</div>
          </div>
        </div>

        {/* ── Streak — live games only ── */}
        {!isArchiveReplay && streak && streak.current >= 1 && (
          <div className="win-streak">
            <Flame size={14} className="streak-icon" />
            <span className="streak-current">{streak.current}-Day Streak</span>
            {streak.best > 1 && (
              <span className="streak-best">Best: {streak.best}</span>
            )}
          </div>
        )}
      </div>

      {/* -- Global Benchmark - live games only -- */}
      {!isArchiveReplay && (
        <div className="global-stats-card">
          <div className="gs-header">
            <BarChart2 size={13} />
            <span>Global Benchmark — Day #{day}</span>
          </div>
          {statsLoading ? (
            <div className="gs-loading">Fetching global data...</div>
          ) : globalStats?.available ? (
            <>
              <div className="gs-grid">
                <div className="gs-item">
                  <div className="gs-item-label">Avg Moves</div>
                  <div className="gs-item-val">{globalStats.avg_moves}</div>
                  <div className={`gs-item-you ${gs.moves <= globalStats.avg_moves ? 'better' : 'worse'}`}>
                    You: {gs.moves}
                  </div>
                </div>
                <div className="gs-item">
                  <div className="gs-item-label">Avg Time</div>
                  <div className="gs-item-val">{fmtLg(globalStats.avg_time)}</div>
                  <div className={`gs-item-you ${elapsed <= globalStats.avg_time ? 'better' : 'worse'}`}>
                    You: {fmtLg(elapsed)}
                  </div>
                </div>
                <div className="gs-item">
                  <div className="gs-item-label">Avg Score</div>
                  <div className="gs-item-val">{globalStats.avg_score}</div>
                  <div className={`gs-item-you ${(gs.score || 0) >= globalStats.avg_score ? 'better' : 'worse'}`}>
                    You: {isLost ? 0 : (gs.score || 0)}
                  </div>
                </div>
                <div className="gs-item">
                  <div className="gs-item-label">Win Rate</div>
                  <div className="gs-item-val">{Math.round((globalStats.wins / globalStats.total_plays) * 100)}%</div>
                  <div className="gs-item-you neutral">
                    {globalStats.total_plays.toLocaleString()} plays
                  </div>
                </div>
              </div>
              <div className="gs-footer">
                <Users size={11} />
                <span>{globalStats.total_plays.toLocaleString()} people have solved this case so far.</span>
              </div>
            </>
          ) : globalStats && !globalStats.available ? (
            <div className="gs-unlocking">
              <div className="gs-unlocking-bar">
                <div
                  className="gs-unlocking-fill"
                  style={{ width: `${Math.min(100, Math.round(((globalStats.total_plays || 0) / (globalStats.threshold || 50)) * 100))}%` }}
                />
              </div>
              <div className="gs-unlocking-text">
                Global benchmark unlocks after {globalStats.threshold || 50} plays.
                <br />
                <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                  Check back later to see how the world performed.
                </span>
                <div style={{ marginTop: 4, fontWeight: 500 }}>
                  {(globalStats.total_plays || 0) > 0 ? `${globalStats.total_plays} of ${globalStats.threshold || 50} completed` : 'Be one of the first to solve today\'s case.'}
                </div>
              </div>
            </div>
          ) : (
            <div className="gs-loading">Global benchmark will appear here as more players solve this case.</div>
          )}
        </div>

      )}
      {/* ─── Community Card ─── */}
      <CommunityCard isEndScreen={true} />

      {/* -- Share Card - live games only -- */}
      {!isArchiveReplay && (
        <div className="share-card">
          <div className="share-top">
            <span className="share-label">Share Result</span>
            <button
              className={`share-copy-btn${copiedShare ? ' copied' : ''}`}
              onClick={copyResults}
            >
              {copiedShare ? <><CheckCircle size={13} /> Copied!</> : <><Share2 size={13} /> Copy & Share</>}
            </button>
          </div>
          <div className="share-text-box">
            {txtShare.split('\n').map((ln, i) => {
              let c = 'sh-line';
              if (i === 0) c = 'sh-hi';
              else if (ln.includes('Grade:')) c = 'sh-stats';
              else if (ln.includes('move')) c = 'sh-dim';
              else if (ln.includes('🟩') || ln.includes('🟧') || ln.includes('🟥')) c = 'sh-boxes';
              else if (ln.startsWith('#')) c = 'sh-tags';
              return <div key={i} className={c}>{ln || ' '}</div>;
            })}
          </div>
        </div>
      )}

      {/* Case Log — always shown */}
      <div className="share-card" style={{ marginTop: 16 }}>
        <div className="share-top" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 8, marginBottom: 12 }}>
          <span className="share-label">Case Log</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {(gs.feed || []).map((e, i) => (
            <ResultEntry key={i} entry={e} isLatest={false} />
          ))}
        </div>
      </div>

      <CaseDebrief caseInfo={caseInfo} diagnosis={isLost ? caseInfo?.exact_diagnosis : gs.diagnosis} />
    </div>
  );
});



// ─────────────────────────────────────────────────────────────────────────────
// ARCHIVE — All past days fetched from server + local progress from storage
// ─────────────────────────────────────────────────────────────────────────────
function ArchivePage({ onReplay }) {
  const [cases, setCases] = useState(null); // null = loading
  const [err, setErr] = useState(null);
  // Read local progress once on mount
  const localProgress = useMemo(() => {
    const arc = getArchive();
    const map = {};
    arc.forEach(e => { map[e.day] = e; });
    return map;
  }, []);

  useEffect(() => {
    fetch('/api/archive')
      .then(r => r.json())
      .then(d => setCases(d.cases || []))
      .catch(() => setErr('Could not load archive. Is the server running?'));
  }, []);

  if (cases === null) return (
    <div className="page-shell">
      <div className="page-head">Archive</div>
      <div className="arc-empty"><div className="spinner" style={{ margin: '0 auto' }} /></div>
    </div>
  );

  return (
    <div className="page-shell">
      <div className="page-head">Archive — Past Cases</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--low)', marginBottom: 16 }}>
        Your daily progress is saved locally. Tap any case to replay.
      </div>
      {err && <div className="arc-empty" style={{ color: 'var(--red)' }}>{err}</div>}
      {!err && cases.length === 0 && (
        <div className="arc-empty">
          <div style={{ marginBottom: 12 }}><Archive size={40} color="var(--low)" /></div>
          No past cases yet — come back tomorrow!
        </div>
      )}
      {!err && cases.length > 0 && (
        <div className="arc-grid">
          {cases.map((item) => {
            const prog = localProgress[item.day];
            return (
              <div
                className={`arc-card${prog ? ' arc-card-done' : ''}`}
                key={item.day}
                onClick={() => onReplay(item.day)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onReplay(item.day)}
              >
                <div className="arc-card-top-row">
                  <div className="arc-card-day">Day #{item.day}</div>
                </div>
                <div className="arc-card-cc">
                  {item.age && item.sex ? `${item.age}${item.sex} — ` : ''}
                  {(item.chief_complaint || 'Unknown case').slice(0, 60)}
                  {(item.chief_complaint || '').length > 60 ? '…' : ''}
                </div>
                {prog && (
                  <div className="arc-card-prog">
                    <span style={{ color: 'var(--green)' }}>✓ Attempted</span>
                  </div>
                )}
                <div className="arc-card-play"><Play size={14} /> {prog ? 'Replay' : 'Play'}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// HOME PAGE
// ─────────────────────────────────────────────────────────────────────────────
function HowToPlayModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={20} /></button>
        <div className="modal-title">How to Play</div>
        <div className="home-rules">
          <div className="rule-item">
            <div className="rule-icon"><Clock size={16} /></div>
            <div><strong>02:30 Limit:</strong> You have 150 seconds to nail the diagnosis. No extensions.</div>
          </div>
          <div className="rule-item">
            <div className="rule-icon"><Activity size={16} /></div>
            <div><strong>Start at 100:</strong> Every unnecessary test costs −10 pts. Trap moves cost −10. Time drains up to −10 pts total.</div>
          </div>
          <div className="rule-item">
            <div className="rule-icon"><Trophy size={16} /></div>
            <div><strong>Grades:</strong> S (90+) · A (75–89) · B (55–74) · C (30–54) · D (1–29) · F (time expired)</div>
          </div>
          <div className="rule-item">
            <div className="rule-icon"><ShieldAlert size={16} /></div>
            <div><strong>Zero Mercy:</strong> Time expires → patient to ICU → you lose. Score: 0.</div>
          </div>
        </div>
        <div className="modal-contact" style={{ marginTop: 24, fontSize: '0.85rem', color: 'var(--mid)', textAlign: 'center' }}>
          Questions? Please reach out to us at <a href="https://x.com/TimePass685909" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)', fontWeight: 600 }}>X</a>
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicyPage({ onBack }) {
  return (
    <div className="home-shell">
      <div className="home-content" style={{ maxWidth: 700, alignItems: 'flex-start', textAlign: 'left' }}>
        <button className="nav-btn" onClick={onBack} style={{ marginBottom: 20 }}>
          <ChevronRight size={16} style={{ display: 'inline', marginRight: 4, transform: 'rotate(180deg)' }} /> Back to Home
        </button>

        <div className="modal-title" style={{ color: 'var(--green)', alignSelf: 'flex-start' }}>
          <ShieldCheck size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: '-4px' }} />
          Privacy Policy
        </div>

        <div className="privacy-body" style={{ marginTop: 16 }}>
          <p className="privacy-date">Effective Date: May 4, 2026 &nbsp;·&nbsp; Last Updated: May 5, 2026</p>
          <p style={{ color: 'var(--mid)', fontSize: '0.85rem', marginBottom: 24 }}>
            The Differential is a free, daily clinical diagnosis puzzle. This policy explains clearly and honestly what data we collect, what we do not collect, and how your information is handled. We will update this page whenever anything changes.
          </p>

          <div className="privacy-section">
            <div className="privacy-sh">What We Collect — Anonymous Game Stats Only</div>
            <p>When you finish a case, we record a small, fully anonymous data point on our servers. This includes:</p>
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 1.8 }}>
              <li>Which day's case was played</li>
              <li>Whether the game was won or lost</li>
              <li>Number of diagnostic moves ordered</li>
              <li>Time taken to complete the case</li>
              <li>Final score and grade</li>
            </ul>
            <p style={{ marginTop: 8 }}>
              This data is used exclusively to power the <strong>Global Benchmark</strong> — the community averages shown after you complete a case. It is aggregated and never tied to an individual.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">What We Do NOT Collect</div>
            <p>We have deliberately built The Differential to collect as little as possible. We do <strong>not</strong> collect or store:</p>
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 1.8 }}>
              <li>Your name, email address, or any contact information</li>
              <li>Your IP address or device identifiers (anonymized in analytics)</li>
              <li>Personal identifiers for advertising</li>
              <li>Location data</li>
              <li>Browser fingerprints or user agent strings</li>
              <li>Any personally identifiable information (PII)</li>
            </ul>
            <p style={{ marginTop: 8 }}>There is no account system, no login, and no user profile — by design.</p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Your Progress Lives on Your Device</div>
            <p>Your game history, daily progress, streaks, and archive are stored exclusively in your browser's <strong>local storage</strong>. This means:</p>
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 1.8 }}>
              <li>Your data never leaves your device for personal storage purposes</li>
              <li>We cannot access or recover your game history</li>
              <li><strong>Clearing your browser history, site data, or local storage will permanently delete all your progress.</strong> This cannot be undone.</li>
              <li>Uninstalling the PWA or switching browsers will also reset your progress</li>
            </ul>
            <p style={{ marginTop: 8, color: 'var(--amber)', fontWeight: 600, fontSize: '0.85rem' }}>
              Note: When we introduce optional account sign-in (e.g., Google Sign-In) in a future update, your progress will optionally sync to the cloud. Until then, your device is the only place your history exists.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Analytics — Google Analytics (GA4)</div>
            <p>
              We use Google Analytics (GA4) to understand overall site traffic and performance. This helps us see how many clinicians are playing, which cases are most popular, and if the site is loading fast enough for everyone. Data collected through GA4 is aggregated and includes:
            </p>
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 1.8 }}>
              <li>Page views and interaction events</li>
              <li>Approximate geographic location (City/Country level)</li>
              <li>Device and browser type (e.g., iPhone/Chrome)</li>
              <li>Site performance and loading speed metrics</li>
            </ul>
            <p style={{ marginTop: 8 }}>
              Google Analytics uses cookies to distinguish users, but we have configured it to avoid collecting personal information.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Advertising</div>
            <p>
              We do not currently display any advertisements. If we introduce advertising in the future, this policy will be updated to describe the specific partners and data practices involved — before any ads go live.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Third-Party Services</div>
            <p>
              The Differential runs on a self-hosted backend. We do not share your data with any third-party platforms, data brokers, or analytics services beyond what is described in this policy.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Children's Privacy</div>
            <p>
              The Differential is intended for medical students, doctors, and clinical professionals. It is not directed at children under 13. We do not knowingly collect any information from children.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">Policy Updates</div>
            <p>
              This policy may be updated as the product evolves. The effective date at the top of this page will always reflect the latest version. Significant changes — such as the introduction of accounts or advertising — will be communicated on the home page before they take effect.
            </p>
          </div>

          <div className="privacy-section" style={{ borderBottom: 'none' }}>
            <div className="privacy-sh">Contact</div>
            <p>
              Questions? Please reach out to us at <a href="https://x.com/TimePass685909" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)', fontWeight: 600 }}>X</a>.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

function TermsPage({ onBack }) {
  return (
    <div className="home-shell">
      <div className="home-content" style={{ maxWidth: 700, alignItems: 'flex-start', textAlign: 'left' }}>
        <button className="nav-btn" onClick={onBack} style={{ marginBottom: 20 }}>
          <ChevronRight size={16} style={{ display: 'inline', marginRight: 4, transform: 'rotate(180deg)' }} /> Back to Home
        </button>

        <div className="modal-title" style={{ color: 'var(--green)', alignSelf: 'flex-start' }}>
          <FileText size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: '-4px' }} />
          Terms and Conditions
        </div>

        <div className="privacy-body" style={{ marginTop: 16 }}>
          <p className="privacy-date">Effective Date: May 4, 2026 &nbsp;·&nbsp; Last Updated: May 4, 2026</p>
          <p style={{ color: 'var(--mid)', fontSize: '0.85rem', marginBottom: 24 }}>
            By using The Differential, you agree to these terms. Please read them carefully. The Differential is provided as a clinical reasoning tool and daily challenge for medical professionals and students.
          </p>

          <div className="privacy-section">
            <div className="privacy-sh">1. Educational Purpose Only</div>
            <p>
              The Differential is a clinical simulation. All cases, patients, and findings are fictional — but we take accuracy seriously. <strong>Every case is carefully designed and medically reviewed to ensure clinical realism.</strong> Nothing in this game constitutes medical advice, diagnosis, or treatment. The content is for educational and entertainment purposes only.
            </p>
            <p style={{ marginTop: 8 }}>
              Real-world medical decisions should always be based on current clinical guidelines and the judgment of a qualified healthcare professional.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">2. No Liability</div>
            <p>
              The creators and developers of The Differential assume no liability for any actions taken or not taken based on the content of this game. You use this site at your own risk.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">3. Intellectual Property</div>
            <p>
              All game mechanics, logic, case content, and visual design are the property of The Differential. You may share your results and screenshots for personal or educational use, but commercial redistribution of game content is prohibited.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">4. User Progress</div>
            <p>
              As detailed in our Privacy Policy, your progress is stored locally on your device. We are not responsible for data loss caused by clearing browser history, site data, or switching devices.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">5. Fair Play</div>
            <p>
              The Differential is built on community trust. We ask that users do not spoil the daily case on public forums before the cycle ends, as it diminishes the experience for others.
            </p>
          </div>

          <div className="privacy-section">
            <div className="privacy-sh">6. Changes to Terms</div>
            <p>
              We may update these terms as the game evolves. Your continued use of the site after updates indicates acceptance of the revised terms.
            </p>
          </div>

          <div className="privacy-section" style={{ borderBottom: 'none' }}>
            <div className="privacy-sh">7. Contact</div>
            <p>
              Questions? Please reach out to us at <a href="https://x.com/TimePass685909" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)', fontWeight: 600 }}>X</a>.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

const HomePage = React.memo(({ caseInfo, gs, onPlay, onPrivacy, onTerms, nextCaseTime, onLogoClick }) => {
  const [showRules, setShowRules] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const streak = useMemo(() => getStreakData(), []);
  const todayDone = gs?.status === 'won' || gs?.status === 'lost';

  // PWA install prompt event
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Enter key → play
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter' && !showRules) onPlay();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPlay, showRules]);



  return (
    <div className="home-shell">
      {showRules && <HowToPlayModal onClose={() => setShowRules(false)} />}

      {/* Offline banner */}
      {isOffline && (
        <div className="offline-banner">
          <WifiOff size={14} />
          <span>You're offline. Connect to load today's case. Your progress is saved locally.</span>
        </div>
      )}

      <div className="home-content">
        <div className="home-logo-container">
          <div className="home-logo" onClick={onLogoClick} style={{ cursor: 'pointer' }}>
            <span className="logo-the">The </span>
            <span className="logo-diff">DIFF</span>
            <span className="logo-rest">erential</span>
          </div>
        </div>
        <div className="home-desc">A hardcore clinical puzzle game for the sharpest minds.</div>

        {/* Streak callout */}
        {streak.current >= 1 && (
          <div className="home-streak">
            <Flame size={14} className="streak-icon" />
            <span>You're on a <strong>{streak.current}-day streak</strong></span>
            {streak.best > streak.current && (
              <span className="streak-best">Best: {streak.best}</span>
            )}
          </div>
        )}

        <div className="home-actions">
          {todayDone ? (
            <>
              <button className="play-btn" onClick={onPlay}>
                <Trophy size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px' }} />
                View Today's Results
              </button>
            </>
          ) : (
            <button className="play-btn" onClick={onPlay}>
              <Play size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px', fill: 'currentColor' }} />
              Play Today's Case (Day #{caseInfo?.day})
            </button>
          )}
          <button className="rules-btn" onClick={() => setShowRules(true)}>
            <HelpCircle size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px' }} />
            How to Play
          </button>
        </div>

        <div className="next-case">
          Next cycle (13:00 GMT) in: <strong>{nextCaseTime}</strong>
        </div>

        {/* ─── Community Card ─── */}
        <CommunityCard />
      </div>


      {/* About Section */}
      <div className="about-section">
        <div className="about-content">
          <div className="about-head">About The Differential</div>
          <div className="about-body">
            <p>
              Medicine is not just about knowing facts — it's about thinking under pressure.
              The Differential is a daily clinical puzzle game that challenges you to reach the correct
              diagnosis using as few tests as possible, against the clock.
            </p>
            <p>
              Each case is built around real disease presentations, authentic clinical findings, and
              the kind of diagnostic traps that catch even experienced physicians off guard. Every case
              is medically reviewed before it goes live — because getting the clinical details right is
              something we take seriously. The game rewards parsimony — the art of doing more with less — and
              punishes the reflexive "order everything" approach that plagues modern medicine.
            </p>
            <p>
              Whether you're a medical student sharpening your differential diagnosis skills, a
              resident keeping your clinical instincts sharp, or simply someone fascinated by the
              detective work of medicine — there's something here for you. Every case is a lesson
              wrapped in a challenge.
            </p>
            <p>
              A new case drops every day. Come back tomorrow.
            </p>
            <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--mid)' }}>
              Questions? Please reach out to us at <a href="https://x.com/TimePass685909" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)', fontWeight: 600 }}>X</a>.
            </p>
          </div>
          <div className="about-disclaimer">
            <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
            Disclaimer: The Differential is for entertainment and educational purposes only.
            All cases are fictional simulations. We care deeply about clinical accuracy — every case
            is medically reviewed before it goes live.
            Nothing in this game constitutes medical advice, diagnosis, or treatment.
            Always consult a qualified healthcare professional for real medical decisions.
          </div>
        </div>
      </div>

      <div className="home-footer">
        <button className="footer-link" onClick={onPrivacy}>Privacy Policy</button>
        <span className="footer-sep">·</span>
        <button className="footer-link" onClick={onTerms}>Terms of Service</button>
        <span className="footer-sep">·</span>
        <a href="https://x.com/TimePass685909" target="_blank" rel="noopener noreferrer" className="footer-link">X</a>
      </div>
    </div>
  );
});

const IDLE_LINES = [
  "The patient is still waiting.",
  "Clinical inertia is not a diagnosis.",
  "Your resident solved this three minutes ago.",
  "The attending walked by. Said nothing. Worse.",
  "Time is a clinical variable too.",
];

// ── Editor Gate ──────────────────────────────────────────────────────────────
// Render the secure editor only on /editor AND only for real human browsers.
// Bots, crawlers and automated scanners get the regular app (bot guard above).
const AppOrEditor = IS_EDITOR_MODE
  ? require('./Editor').default
  : _App;
export default AppOrEditor;

function _App() {
  // Theme — dark/light persisted to localStorage
  const [theme, toggleTheme] = useTheme();

  // If the browser navigated to /privacy or /terms directly, honour that as starting tab
  const [tab, setTab] = useState(() => {
    if (IS_PRIVACY_PAGE) return 'privacy';
    if (IS_TERMS_PAGE) return 'terms';
    return 'home';
  });
  const [caseInfo, setCaseInfo] = useState(null);
  const [gs, setGs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Command
  const [cmd, setCmd] = useState('');
  const [cmdBusy, setCmdBusy] = useState(false);
  const [cmdErr, setCmdErr] = useState(null);
  const [selSug, setSelSug] = useState(null);

  // Suggestions
  const [sugs, setSugs] = useState(null);
  const [hiIdx, setHiIdx] = useState(-1);
  const sugTimer = useRef(null);

  // Guess
  const [guess, setGuess] = useState('');
  const [gBusy, setGBusy] = useState(false);
  const [gErr, setGErr] = useState(null);

  // Diseases typeahead
  const [diseases, setDiseases] = useState([]);
  const [guessSugs, setGuessSugs] = useState(null);
  const [hiGuessIdx, setHiGuessIdx] = useState(-1);

  // Timer / idle
  const [elapsed, setElapsed] = useState(0);
  const [taunt, setTaunt] = useState(null);
  const idleRef = useRef(null);

  // The latest feed entry (drives the comment strip)

  // Archive replay state
  const [archiveReplay, setArchiveReplay] = useState(null); // { day, caseInfo }

  const cmdRef = useRef(null);
  const guessRef = useRef(null);

  // ─── Load
  useEffect(() => {
    (async () => {
      try {
        const dRes = await fetchDiseases();
        if (dRes.diseases) setDiseases(dRes.diseases);

        const data = await fetchGame();
        if (data.dx_b64) {
          try { data.exact_diagnosis = atob(data.dx_b64); } catch (e) { }
        }
        setCaseInfo(data);

        // Remap lastDay if it was stored during the Day 100 → 1 wrap, so
        // streaks keep counting 101, 102, … instead of resetting.
        reconcileStreak(data.day);

        // If editor launched a ?playDay=N preview, start that day's replay
        if (PLAY_DAY_OVERRIDE) {
          // Don't set main game state — startArchiveReplay handles it
          const fresh = initGameState(data.day);
          setGs(fresh);
          // Trigger archive replay for the specific day
          return; // startArchiveReplay called below after mount
        }

        const saved = loadGameState();
        if (saved && saved.day === data.day) {
          // HEALING: If game is finished but missing the lock-in comments (legacy), pick and save them once now.
          if (saved.status === 'lost' && !saved.final_status_message) {
            saved.final_status_message = getRandomLosingComment();
            saveGameState(saved);
          }
          setGs(saved);
          if (saved.status === 'playing') setElapsed(getElapsedSeconds(saved.start_time));
        } else {
          const fresh = initGameState(data.day);
          setGs(fresh);
          saveGameState(fresh);
        }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // If opened with ?playDay=N (from editor), auto-start that day's archive replay
  useEffect(() => {
    if (!PLAY_DAY_OVERRIDE || loading) return;
    startArchiveReplay(PLAY_DAY_OVERRIDE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ─── Start Game (today's daily case)
  // IMPORTANT: If today's game is already finished (won/lost), we MUST show the
  // results screen — never restart. Replaying today's case is not allowed.
  const startGame = useCallback(() => {
    setArchiveReplay(null);
    setCmd('');
    setGuess('');
    setCmdErr(null);
    setGErr(null);
    setSugs(null);
    setGuessSugs(null);

    // Always load today's saved state first
    const saved = loadGameState();
    const todayGs = (saved && caseInfo && saved.day === caseInfo.day) ? saved : null;

    if (todayGs) {
      // ── Game already finished: go directly to results, do NOT reset ──
      if (todayGs.status === 'won' || todayGs.status === 'lost') {
        setGs(todayGs);
        setElapsed(0);
        setTab('game');
        return;
      }

      // ── Game in progress ──
      let activeGs = todayGs;
      if (todayGs.status === 'waiting') {
        // First time entering — transition to playing and start timer
        activeGs = { ...todayGs, status: 'playing', start_time: Date.now() };
        saveGameState(activeGs);
      }
      setGs(activeGs);
      if (activeGs.status === 'playing') setElapsed(getElapsedSeconds(activeGs.start_time));
    } else if (caseInfo) {
      // ── No save yet for today — start fresh ──
      const fresh = initGameState(caseInfo.day);
      const ns = { ...fresh, status: 'playing', start_time: Date.now() };
      setGs(ns);
      saveGameState(ns);
    }

    setTab('game');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseInfo]);

  // ─── Archive Replay
  const startArchiveReplay = useCallback(async (day) => {
    try {
      // Snapshot today's live state before we overwrite gs with archive data.
      // This ensures the save-useEffect can never see the archive gs as the live game.
      if (caseInfo && gs && !archiveReplay) {
        saveGameState(gs);
      }

      const data = await fetchGameByDay(day);
      if (data.dx_b64) {
        try { data.exact_diagnosis = atob(data.dx_b64); } catch (e) { }
      }
      const freshGs = { ...initGameState(day), status: 'playing', start_time: Date.now() };
      setArchiveReplay({ day, caseInfo: data });
      setGs(freshGs);
      setElapsed(0);
      setCmd('');
      setGuess('');
      setCmdErr(null);
      setGErr(null);
      setSugs(null);
      setTab('game');
    } catch (e) {
      console.error('Archive replay failed:', e);
      alert(`Could not load Day #${day}: ${e.message}`);
    }
  }, [caseInfo, gs, archiveReplay]);

  // ─── Go Home (from case page back button)
  // Clears archive replay context and restores today's gs from localStorage
  // so the stale archive gs NEVER triggers the save-useEffect against today's slot.
  const goHome = useCallback(() => {
    setArchiveReplay(null);

    // Immediately restore today's persisted state into gs.
    // This must happen BEFORE setArchiveReplay's effect flushes, so the
    // save-useEffect (which runs when gs+archiveReplay change) sees the
    // correct live state rather than the archive's gs.
    if (caseInfo) {
      const saved = loadGameState();
      if (saved && saved.day === caseInfo.day) {
        setGs(saved);
      }
    }

    setCmd('');
    setGuess('');
    setCmdErr(null);
    setGErr(null);
    setSugs(null);
    setGuessSugs(null);
    setTab('home');
  }, [caseInfo]);

  // ─── Check if the current archive replay day was previously attempted
  const archiveAttempted = useMemo(() => {
    if (!archiveReplay) return false;
    const arc = getArchive();
    return arc.some(e => e.day === archiveReplay.day);
  }, [archiveReplay]);

  // ─── Timer
  useEffect(() => {
    if (!gs || gs.status !== 'playing') return;
    const iv = setInterval(() => {
      const e = getElapsedSeconds(gs.start_time);
      setElapsed(e);
      if (e >= 150) {
        clearInterval(iv);
        const statusMsg = getRandomLosingComment();
        const ns = {
          ...gs,
          status: 'lost',
          end_time: Date.now(),
          final_status_message: statusMsg
        };
        setGs(ns);
        if (!archiveReplay) {
          saveGameState(ns);
        }
        pushToArchive({
          day: gs.day,
          date: new Date().toISOString().split('T')[0],
          diagnosis: caseInfo?.exact_diagnosis || 'Unknown',
          score: 0,
          grade: 'F',
          moves: gs.moves,
          time: 150,
          fast_solve: false,
          status: 'lost',
          final_status_message: statusMsg
        });
      }
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gs, caseInfo]);

  // ─── Next Case Timer & Auto-Reload
  const [nextCaseTime, setNextCaseTime] = useState('');
  useEffect(() => {
    let lastSec = -1;

    function calcNext() {
      const now = new Date();

      // Roll over at 13:00 UTC (18:30 IST)
      // Anchor: 2026-05-05T13:00:00Z
      const anchor = new Date('2026-05-05T13:00:00Z');

      // Calculate how many full days have passed since anchor
      const msDiff = now - anchor;
      let days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
      if (msDiff < 0) days = -1; // Before anchor, we are in the first cycle (Day 1)

      // Next rollover is at anchor + (N) days, where N is at least 1 
      // and increases after each 24h block from the anchor.
      const nextRolloverCount = Math.max(0, days) + 1;
      const next = new Date(anchor.getTime() + nextRolloverCount * 24 * 60 * 60 * 1000);

      const ms = next - now;
      const s = Math.floor(Math.max(0, ms) / 1000);
      if (s === lastSec) return;
      lastSec = s;

      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const rs = s % 60;
      setNextCaseTime(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${rs.toString().padStart(2, '0')}`);
    }
    calcNext();
    const iv = setInterval(calcNext, 500);
    return () => clearInterval(iv);
  }, []);

  // ─── NO autofocus — user initiates input themselves
  // Inputs focus only on explicit user tap/click

  // ─── Idle
  const resetIdle = useCallback(() => {
    setTaunt(null);
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => {
      setTaunt(IDLE_LINES[Math.floor(Math.random() * IDLE_LINES.length)]);
    }, 45000);
  }, []);
  useEffect(() => { resetIdle(); return () => clearTimeout(idleRef.current); }, [resetIdle]);

  // ─── Save
  useEffect(() => {
    // Never save to localStorage during archive replays or preview mode
    if (gs && !archiveReplay) saveGameState(gs);
  }, [gs, archiveReplay]);

  // ─── Suggestions
  const onCmdChange = useCallback(val => {
    setCmd(val); setSelSug(null); setHiIdx(-1); setCmdErr(null);
    clearTimeout(sugTimer.current);
    if (!val || val.trim().length < 1) { setSugs(null); return; }
    sugTimer.current = setTimeout(async () => {
      const d = await fetchSuggestions(val);
      setSugs(d.suggestions || []);
    }, 100);
  }, []);

  const pickSug = useCallback(s => {
    setSelSug(s); setCmd(s.displayName); setSugs(null); setHiIdx(-1); setCmdErr(null);
  }, []);

  // ─── Order action
  const doAction = useCallback(async () => {
    const input = (selSug?.displayName || cmd).trim();
    if (!input || cmdBusy) return;
    if (!selSug && sugs !== null && sugs.length === 0) {
      setCmdErr('Not a recognised test. Pick from the dropdown or check spelling.'); return;
    }
    setCmdBusy(true); setCmdErr(null); setSugs(null);
    resetIdle();
    try {
      // Always pass the current case day so server evaluates the right case
      const activeDay = archiveReplay ? archiveReplay.day : (caseInfo?.day || null);
      const res = await sendAction(input, gs.ordered, gs.used_snark_indices || {}, null, activeDay);
      if (res.type === 'unknown') { setCmdErr(res.message); return; }
      const entry = { ...res, action: input, timestamp: Date.now() };
      setGs(prev => {
        const slug = (res.display_name || input).toLowerCase().replace(/\s+/g, '_');
        return {
          ...prev,
          moves: prev.moves + (res.moves_delta || 0),
          ordered: [...prev.ordered, slug],
          feed: [...prev.feed, entry],
          used_snark_indices: res.usedSnarkIndices || prev.used_snark_indices || {},
        };
      });
      setCmd(''); setSelSug(null);
    } catch (e) { setCmdErr(e.message); }
    finally { setCmdBusy(false); setTimeout(() => cmdRef.current?.blur(), 10); }
  }, [cmd, selSug, cmdBusy, gs, sugs, resetIdle, archiveReplay, caseInfo]);

  const onCmdKey = e => {
    if (sugs && sugs.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHiIdx(i => Math.min(i + 1, sugs.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHiIdx(i => Math.max(i - 1, -1)); return; }
      if (e.key === 'Tab') { e.preventDefault(); const idx = hiIdx >= 0 ? hiIdx : 0; if (sugs[idx]) pickSug(sugs[idx]); return; }
    }
    if (e.key === 'Enter') { if (sugs && hiIdx >= 0 && sugs[hiIdx]) { pickSug(sugs[hiIdx]); return; } doAction(); }
    if (e.key === 'Escape') { setSugs(null); setCmd(''); setSelSug(null); setCmdErr(null); }
  };

  // ─── Guess
  const doGuess = useCallback(async () => {
    const input = guess.trim();
    if (!input || gBusy) return;
    setGBusy(true); setGErr(null);
    setTimeout(() => guessRef.current?.blur(), 10);
    try {
      // Always pass the current case day so server evaluates the right case
      const activeDay = archiveReplay ? archiveReplay.day : (caseInfo?.day || null);
      const res = await submitGuess(input, gs.moves, elapsed, null, activeDay);
      if (res.correct) {
        const gradeL = getGradeLabel(res.score);
        const ns = {
          ...gs, status: 'won', diagnosis: res.exact_diagnosis,
          win_message: res.message, score: res.score, fast_solve: res.fast_solve,
          end_time: Date.now(), case_analysis: res.case_analysis || null
        };
        setGs(ns);
        if (!archiveReplay) {
          saveGameState(ns);
        }
        const elapsed2 = (ns.end_time && ns.start_time) ? Math.floor((ns.end_time - ns.start_time) / 1000) : 0;
        pushToArchive({
          day: activeDay,
          date: new Date().toISOString().split('T')[0],
          diagnosis: res.exact_diagnosis,
          score: res.score,
          grade: gradeL,
          moves: gs.moves,
          time: elapsed2,
          fast_solve: res.fast_solve,
          status: 'won',
          final_status_message: res.message,
        });
        if (!archiveReplay) {
          saveToLeaderboard({
            day: caseInfo.day, moves: gs.moves, time: elapsed,
            score: res.score, date: new Date().toISOString().split('T')[0]
          });
        }
      } else {
        setGErr(null);
        // Penalize a wrong diagnosis with +3 moves (equiv to -30 pts)
        const movesDelta = 3;
        const entry = { type: 'snark', action: `Diagnosed: ${input}`, comment: res.message, moves_delta: movesDelta, clown_penalty: false, timestamp: Date.now() };
        setGs(prev => ({
          ...prev,
          moves: prev.moves + movesDelta,
          guesses: (prev.guesses || 0) + 1,
          feed: [...prev.feed, entry]
        }));
      }
      setGuess('');
    } catch (e) { setGErr(e.message); }
    finally { setGBusy(false); }
  }, [guess, gBusy, gs, elapsed, caseInfo, archiveReplay]);

  const onGuessChange = useCallback(val => {
    setGuess(val);
    setHiGuessIdx(-1);
    setGErr(null);
    if (!val || val.trim().length < 2) {
      setGuessSugs(null);
      return;
    }
    const q = val.toLowerCase().trim();
    
    // Prioritize results: 
    // 1. Starts with query
    // 2. Contains query at start of a word (e.g. "(MI)")
    // 3. Just contains query
    const matches = diseases.filter(d => d.toLowerCase().includes(q));
    
    const sorted = matches.sort((a, b) => {
      const al = a.toLowerCase();
      const bl = b.toLowerCase();
      
      const aStartsWith = al.startsWith(q);
      const bStartsWith = bl.startsWith(q);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      const aWordStart = al.includes(' ' + q) || al.includes('(' + q);
      const bWordStart = bl.includes(' ' + q) || bl.includes('(' + q);
      if (aWordStart && !bWordStart) return -1;
      if (!aWordStart && bWordStart) return 1;
      
      return al.localeCompare(bl);
    });

    setGuessSugs(sorted.slice(0, 12));
  }, [diseases]);

  const pickGuessSug = useCallback(s => {
    setGuess(s);
    setGuessSugs(null);
    setHiGuessIdx(-1);
    setGErr(null);
    guessRef.current?.focus();
  }, []);

  const onGuessKey = e => {
    if (guessSugs && guessSugs.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHiGuessIdx(i => Math.min(i + 1, guessSugs.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHiGuessIdx(i => Math.max(i - 1, -1)); return; }
      if (e.key === 'Tab') { e.preventDefault(); const idx = hiGuessIdx >= 0 ? hiGuessIdx : 0; if (guessSugs[idx]) pickGuessSug(guessSugs[idx]); return; }
    }
    if (e.key === 'Enter') {
      if (guessSugs && hiGuessIdx >= 0 && guessSugs[hiGuessIdx]) { pickGuessSug(guessSugs[hiGuessIdx]); return; }
      doGuess();
    }
    if (e.key === 'Escape') { setGuessSugs(null); setGuess(''); setGErr(null); }
  };

  // ─── Derived
  const isWon = gs?.status === 'won' || gs?.status === 'lost';
  const isPlaying = gs?.status === 'playing';
  const isFixedLayout = tab === 'game' && !isWon;

  return (
    <div className={`shell ${isFixedLayout ? 'fixed' : 'scrollable'}`}>
      {PLAY_DAY_OVERRIDE && (
        <div className="preview-banner">
          🎮 Editor Preview — Day #{PLAY_DAY_OVERRIDE}. Score not saved.
        </div>
      )}

      {/* ── HOME NAV — logo left, Archive right. Shown on home/archive/privacy. */}
      {tab !== 'game' && !loading && (
        <nav className="nav nav-home">
          <div className="nav-logo" onClick={goHome} style={{ cursor: 'pointer' }}>
            <span className="logo-the">The </span>
            <span className="logo-diff">DIFF</span>
            <span className="logo-rest">erential</span>
          </div>
          <div className="nav-actions">
            <button
              className={`nav-btn${tab === 'archive' ? ' active' : ''}`}
              onClick={() => setTab('archive')}
            >
              Archive
            </button>
            <button
              className="nav-btn theme-toggle-btn"
              onClick={toggleTheme}
              aria-label="Toggle light/dark mode"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark'
                ? <Sun size={15} />
                : <Moon size={15} />}
            </button>
          </div>
        </nav>
      )}

      {/* ── CASE NAV — back chevron, Day # [Attempted], Timer. Shown on game page. */}
      {tab === 'game' && !loading && (
        <nav className="nav nav-case">
          <button className="case-back-btn" onClick={goHome} aria-label="Back to home">
            <ChevronLeft size={22} />
          </button>
          <div className="case-nav-center">
            <span className="case-nav-day">
              Day #{(archiveReplay ? archiveReplay.caseInfo : caseInfo)?.day}
            </span>
            {archiveReplay && archiveAttempted && (
              <span className="case-attempted-badge">Attempted</span>
            )}
          </div>
          <div className="case-nav-right">
            {isPlaying && (
              <span className={`case-nav-timer${elapsed > 120 ? ' urgent' : ''}`}>
                {fmt(Math.max(0, 150 - elapsed))}
              </span>
            )}
          </div>
        </nav>
      )}

      {loading && (
        <div className="state-screen">
          <div className="loading-text-logo">
            <span className="logo-the">The </span>
            <span className="logo-diff">DIFF</span>
            <span className="logo-rest">erential</span>
          </div>
          <div className="spinner" />
          <div className="state-lbl">Preparing your shift...</div>
        </div>
      )}

      {/* ERROR */}
      {!loading && error && (
        <div className="state-screen">
          <div className="err-title">Connection Error</div>
          <div className="err-sub">{error}</div>
          {/* <div className="err-sub" style={{ marginTop: 3 }}>Server must be running on port 5000.</div> */}
          <button className="retry-btn" onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* ARCHIVE */}
      {!loading && !error && tab === 'archive' && <ArchivePage onReplay={startArchiveReplay} />}

      {/* HOME */}
      {!loading && !error && tab === 'home' && (
        <HomePage caseInfo={caseInfo} gs={gs}
          onPlay={startGame}
          onPrivacy={() => {
            window.history.pushState({}, '', '/privacy');
            setTab('privacy');
          }}
          onTerms={() => {
            window.history.pushState({}, '', '/terms');
            setTab('terms');
          }}
          nextCaseTime={nextCaseTime} onLogoClick={goHome}
        />
      )}

      {/* PRIVACY — also reachable at /privacy URL */}
      {!loading && !error && tab === 'privacy' && (
        <PrivacyPolicyPage onBack={() => {
          window.history.pushState({}, '', '/');
          setTab('home');
        }} />
      )}

      {/* TERMS — also reachable at /terms URL */}
      {!loading && !error && tab === 'terms' && (
        <TermsPage onBack={() => {
          window.history.pushState({}, '', '/');
          setTab('home');
        }} />
      )}

      {/* GAME */}
      {!loading && !error && tab === 'game' && (
        <>
          {isWon ? (
            <EndScreen gs={gs} day={(archiveReplay?.caseInfo || caseInfo)?.day} caseInfo={archiveReplay?.caseInfo || caseInfo} isArchiveReplay={!!archiveReplay} />
          ) : !gs ? (
            <div className="state-screen"><div className="spinner" /></div>
          ) : (
            <div className="game-area">

              {/* Patient panel — history + exam upfront */}
              <PatientPanel patient={(archiveReplay?.caseInfo || caseInfo)?.patient} />

              {/* Results feed — clinical findings as primary content */}
              <Feed entries={gs?.feed || []} />

              {/* Input dock */}
              <div className="input-dock">

                <div className="cmd-wrap">
                  <div className="cmd-row">
                    <span className="cmd-glyph"><Search size={14} /></span>
                    <input
                      ref={cmdRef}
                      className="cmd-input"
                      type="text"
                      value={cmd}
                      onChange={e => onCmdChange(e.target.value)}
                      onKeyDown={onCmdKey}
                      onFocus={() => { if (cmd.trim().length >= 1) onCmdChange(cmd); }}
                      onBlur={() => setTimeout(() => setSugs(null), 150)}
                      placeholder={cmdBusy ? 'Processing...' : 'Order a test, lab, or exam...'}
                      disabled={cmdBusy}
                      maxLength={200}
                      autoComplete="off"
                      spellCheck="false"
                    />
                    <button className="cmd-btn" onClick={doAction} disabled={cmdBusy || !cmd.trim()}>
                      {cmdBusy ? '···' : 'Order'}
                    </button>
                  </div>
                  <Suggestions list={sugs} query={cmd} hiIdx={hiIdx} onSelect={pickSug} onHover={setHiIdx} />
                </div>

                {(cmdErr || taunt) && (
                  <div className={`cmd-feedback${taunt && !cmdErr ? ' taunt' : ''}`}>
                    {cmdErr ? <>⚠ {cmdErr}</> : <>⏳ {taunt}</>}
                  </div>
                )}

                <div className="cmd-wrap">
                  <div className="guess-row">
                    <span className="guess-glyph">Δx</span>
                    <input
                      ref={guessRef}
                      className="guess-input"
                      type="text"
                      value={guess}
                      onChange={e => onGuessChange(e.target.value)}
                      onKeyDown={onGuessKey}
                      onBlur={() => setTimeout(() => setGuessSugs(null), 150)}
                      placeholder={(gs?.guesses || 0) > 0 ? `Diagnosis — ${gs.guesses} wrong` : 'Final Diagnosis — submit when confident'}
                      disabled={gBusy}
                      maxLength={200}
                      autoComplete="off"
                      spellCheck="false"
                    />
                    <button className="guess-btn" onClick={doGuess} disabled={gBusy || !guess.trim()}>
                      {gBusy ? '···' : 'Confirm'}
                    </button>
                  </div>

                  {guessSugs && (
                    <div className="sug-drop" style={{ bottom: 'calc(100% + 4px)' }}>
                      {guessSugs.length === 0 ? (
                        <div className="sug-none">Not found in database, but you can still submit this custom entry.</div>
                      ) : (
                        guessSugs.map((s, i) => (
                          <div key={s} className={`sug-item${i === hiGuessIdx ? ' hi' : ''}`}
                            onMouseDown={e => { e.preventDefault(); pickGuessSug(s); }}
                            onMouseEnter={() => setHiGuessIdx(i)}
                          >
                            <span className="sug-icon"><Stethoscope size={12} color="var(--purple)" /></span>
                            <span className="sug-name">{s}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {gErr && <div className="guess-err">✗ {gErr}</div>}
              </div>

            </div>
          )}
        </>
      )}

    </div>
  );
}
